import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { getStudentType, calculateTimelineMessage } from '../../utils/collegeTimelineCalculator';
import { 
  ArrowLeft, 
  ArrowRight, 
  Copy, 
  CheckCircle, 
  Brain, 
  Lightbulb,
  Save,
  RefreshCw,
  User,
  Users,
  Sparkles,
  Target,
  FileText,
  AlertCircle
} from 'lucide-react';

const StudentContextGenerator = () => {
  const navigate = useNavigate();
  const { currentUser, familyUsers, family } = useFamilyContext();
  const { updateMasterStrategy } = useCollegeContext();
  
  const [loading, setLoading] = useState(true);
  const [studentProfile, setStudentProfile] = useState(null);
  const [parentObservations, setParentObservations] = useState({});
  const [parentBObservations, setParentBObservations] = useState({});
  const [personalityProfile, setPersonalityProfile] = useState({});
  const [academicFocus, setAcademicFocus] = useState({});
  const [contextSummary, setContextSummary] = useState('');
  const [localContextSummary, setLocalContextSummary] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [selectedStudentUserId, setSelectedStudentUserId] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Loading student data...');
  const dataLoaded = useRef(false);
  const mounted = useRef(true);
  const renderCount = useRef(0);

  // Track render count
  renderCount.current += 1;
  console.log(`🔍 StudentContextGenerator: Render #${renderCount.current}`, {
    loading,
    error: !!error,
    hasStudentProfile: !!studentProfile,
    contextSummaryLength: contextSummary.length,
    localContextSummaryLength: localContextSummary.length
  });
  
  // Safety check for excessive renders (increased limit for text input)
  if (renderCount.current > 200) {
    console.error('🚨 StudentContextGenerator: Excessive renders detected! Stopping execution.');
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Render Loop Detected</h2>
          <p className="text-slate-600 mb-6">
            The component has rendered too many times. Please refresh the page and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
  
  // Load data once when component mounts
  useEffect(() => {
    console.log('🔍 StudentContextGenerator: useEffect triggered');
    
    if (dataLoaded.current) {
      console.log('🔍 StudentContextGenerator: Data already loaded, skipping');
      return;
    }
    
    if (!currentUser) {
      console.log('🔍 StudentContextGenerator: No current user, skipping');
      return;
    }

    console.log('🔍 StudentContextGenerator: Starting data load');
    dataLoaded.current = true; // Set this immediately to prevent re-runs
    
    const loadStudentData = async () => {
      try {
        console.log('🔍 StudentContextGenerator: loadStudentData started');
        setLoading(true);
        setError(null);
        setLoadingMessage('Loading student data...');
        
        // Get the selected student (similar logic to ParentObservations)
        const savedStudentId = localStorage.getItem('college-selected-student-id');
        console.log('🔍 StudentContextGenerator: savedStudentId:', savedStudentId);
        console.log('🔍 StudentContextGenerator: familyUsers:', familyUsers);
        console.log('🔍 StudentContextGenerator: family.kids:', family?.kids);
        
        let studentUserId = null;
        
        setLoadingMessage('Finding selected student...');
        
        if (savedStudentId) {
          // The savedStudentId is now the auth_user_id, so we can use it directly
          // But we still need to find the student object for name/display purposes
          let student = null;
          if (familyUsers?.length > 0) {
            student = familyUsers.find(u => 
              (u.authUserId || u.auth_user_id) === savedStudentId
            );
            console.log('🔍 StudentContextGenerator: Found in familyUsers:', student);
          } else if (family?.kids?.length > 0) {
            student = family.kids.find(kid => 
              (kid.authUserId || kid.auth_user_id) === savedStudentId
            );
            console.log('🔍 StudentContextGenerator: Found in family.kids:', student);
          }
          
          // The savedStudentId is already the auth_user_id we need
          studentUserId = savedStudentId;
          console.log('🔍 StudentContextGenerator: Using auth_user_id directly:', {
            savedStudentId,
            foundStudent: !!student,
            studentName: student?.name || student?.displayName,
            resolvedUserId: studentUserId
          });
        }
        
        if (!studentUserId) {
          const errorMsg = 'No valid student user ID found. Please ensure a student is selected.';
          console.error(errorMsg);
          setError(errorMsg);
          setLoading(false);
          return;
        }
        
        console.log('🔍 StudentContextGenerator: Loading data for student user_id:', studentUserId);
        setSelectedStudentUserId(studentUserId);
        
        setLoadingMessage('Loading student profile and parent observations...');
        
        // Load student profile and parent observations
        const { data: profileData, error } = await supabase
          .from('student_academic_profiles')
          .select('*')
          .eq('user_id', studentUserId)
          .single();
          
        console.log('🔍 StudentContextGenerator: Database query result - error:', error);
        console.log('🔍 StudentContextGenerator: Database query result - data:', profileData);
        
        if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
          throw error;
        }

        if (profileData) {
          console.log('🔍 StudentContextGenerator: Raw profileData from database:', profileData);
          
          // Set student profile
          const studentData = familyUsers?.find(u => 
                               (u.authUserId || u.auth_user_id) === savedStudentId
                             ) || 
                             family?.kids?.find(kid => 
                               (kid.authUserId || kid.auth_user_id) === savedStudentId
                             );
          const profile = {
            // Basic Info
            name: studentData?.name || studentData?.displayName || 'Student',
            graduation_year: profileData.graduation_year || null,
            grade_level: profileData.grade_level || '',
            state_of_residency: profileData.state_of_residency || '',
            
            // Academic Performance
            gpaWeighted: profileData.gpa_weighted || null,
            gpa_unweighted: profileData.gpa_unweighted || null,
            gpa_scale: profileData.gpa_scale || null,
            class_rank: profileData.class_rank || null,
            class_size: profileData.class_size || null,
            class_rank_percentage: profileData.class_rank_percentage || null,
            
            // Test Scores
            satTotal: profileData.sat_total || null,
            sat_math: profileData.sat_math || null,
            sat_reading_writing: profileData.sat_reading_writing || null,
            actComposite: profileData.act_composite || null,
            act_math: profileData.act_math || null,
            act_english: profileData.act_english || null,
            act_reading: profileData.act_reading || null,
            act_science: profileData.act_science || null,
            psat_total: profileData.psat_total || null,
            national_merit_status: profileData.national_merit_status || '',
            
            // Academic Rigor
            curriculum_rigor: profileData.curriculum_rigor || '',
            ap_courses_taken: profileData.ap_courses_taken || null,
            ap_courses_planned: profileData.ap_courses_planned || null,
            ap_exams_passed: profileData.ap_exams_passed || null,
            ap_courses_details: profileData.ap_courses_details || '',
            dual_enrollment_credits: profileData.dual_enrollment_credits || null,
            honors_courses: profileData.honors_courses || null,
            course_load_description: profileData.course_load_description || '',
            ib_diploma_candidate: profileData.ib_diploma_candidate || false,
            ib_predicted_score: profileData.ib_predicted_score || null,
            
            // Academics & Interests
            academicInterests: Array.isArray(profileData.academic_interests) ? profileData.academic_interests : [],
            academic_interests: Array.isArray(profileData.academic_interests) ? profileData.academic_interests : [],
            careerGoals: profileData.career_goals || '',
            career_goals: profileData.career_goals || '',
            learningStyle: profileData.learning_style || '',
            learning_style: profileData.learning_style || '',
            academicStrengths: Array.isArray(profileData.academic_strengths) ? profileData.academic_strengths : [],
            academic_strengths: Array.isArray(profileData.academic_strengths) ? profileData.academic_strengths : [],
            uniqueQualities: profileData.unique_qualities || '',
            unique_qualities: profileData.unique_qualities || '',
            intendedMajor: profileData.intended_major || '',
            intended_major: profileData.intended_major || '',
            intended_minor: profileData.intended_minor || '',
            
            // Activities & Leadership (both JSONB and legacy text fields)
            extracurriculars: Array.isArray(profileData.extracurriculars) ? profileData.extracurriculars : [],
            leadership_activities: profileData.leadership_activities || '',
            work_experience: profileData.work_experience || '',
            volunteer_work: profileData.volunteer_work || '',
            
            // Awards & Recognition (both JSONB and legacy text fields)
            awards: Array.isArray(profileData.awards) ? profileData.awards : [],
            academic_awards: profileData.academic_awards || '',
            // High School Context
            hs_competitiveness: profileData.hs_competitiveness || '',
            hs_avg_test_scores: profileData.hs_avg_test_scores || '',
            hs_pct_college_bound: profileData.hs_pct_college_bound || null,
            
            // Grade Context
            grade_trend: profileData.grade_trend || '',
            grade_context: profileData.grade_context || '',
            special_talents: profileData.special_talents || '',
            
            // Background & Context
            high_school_name: profileData.high_school_name || '',
            high_school_type: profileData.high_school_type || '',
            high_school_city: profileData.high_school_city || '',
            high_school_state: profileData.high_school_state || '',
            special_circumstances: profileData.special_circumstances || '',
            first_generation_college: profileData.first_generation_college || false,
            legacy_connections: Array.isArray(profileData.legacy_connections) ? profileData.legacy_connections : [],
            underrepresented_background: profileData.underrepresented_background || false,
            
            // Additional Fields
            languages: profileData.languages || '',
            additional_notes: profileData.additional_notes || '',
            student_self_assessment: profileData.student_self_assessment || '',
            college_preferences: profileData.college_preferences || null
          };
          console.log('🔍 StudentContextGenerator: Processed student profile:', profile);
          setStudentProfile(profile);

          // Set parent A observations
          const parentA = {
            easySubject: profileData.parent_observation_easy_subject || '',
            growth: profileData.parent_observation_growth || '',
            learningDifference: profileData.parent_observation_learning_difference || profileData.parent_observation_poor_fit || '',
            surprises: profileData.parent_observation_surprises || '',
            ageLimit: profileData.parent_observation_age_limit || '',
            rulesQuestioned: profileData.parent_observation_rules_questioned || '',
            wrongLevel: profileData.parent_observation_wrong_level || '',
            advocacySuccess: profileData.parent_observation_advocacy_success || '',
            initiativeAgainstAdvice: profileData.parent_observation_initiative_against_advice || '',
            mechanicalSkills: profileData.parent_observation_mechanical_skills || '',
            mathIntegration: profileData.parent_observation_math_integration || '',
            entrepreneurialInstincts: profileData.parent_observation_entrepreneurial_instincts || '',
            selfTaught: profileData.parent_observation_self_taught || '',
            obsessions: profileData.parent_observation_obsessions || '',
            unusualInterests: profileData.parent_observation_unusual_interests || '',
            hiddenQualities: profileData.parent_observation_hidden_qualities || ''
          };
          setParentObservations(parentA);

          // Set parent B observations
          const parentB = {
            easySubject: profileData.parent_b_easy_subject || '',
            growth: profileData.parent_b_growth || '',
            learningDifference: profileData.parent_b_learning_difference || profileData.parent_b_poor_fit || '',
            surprises: profileData.parent_b_surprises || '',
            ageLimit: profileData.parent_b_age_limit || '',
            rulesQuestioned: profileData.parent_b_rules_questioned || '',
            wrongLevel: profileData.parent_b_wrong_level || '',
            advocacySuccess: profileData.parent_b_advocacy_success || '',
            initiativeAgainstAdvice: profileData.parent_b_initiative_against_advice || '',
            mechanicalSkills: profileData.parent_b_mechanical_skills || '',
            mathIntegration: profileData.parent_b_math_integration || '',
            entrepreneurialInstincts: profileData.parent_b_entrepreneurial_instincts || '',
            obsessions: profileData.parent_b_obsessions || '',
            unusualInterests: profileData.parent_b_unusual_interests || '',
            hiddenQualities: profileData.parent_b_hidden_qualities || '',
            differentPerspective: profileData.parent_b_different_perspective || '',
            whatOtherParentMisses: profileData.parent_b_what_other_parent_misses || ''
          };
          setParentBObservations(parentB);

          // Load personality profile if available
          if (profileData.personality_profile) {
            try {
              const savedProfile = JSON.parse(profileData.personality_profile);
              setPersonalityProfile(savedProfile);
              console.log('🔍 StudentContextGenerator: Loaded personality profile:', savedProfile);
            } catch (error) {
              console.log('Error parsing personality profile:', error);
              setPersonalityProfile({});
            }
          }

          // Load academic focus and positioning
          setAcademicFocus({
            focusArea: profileData.assessment_focus_area || 'engineering',
            positioningNarrative: profileData.positioning_narrative || 'institutional_catalyst',
            assessmentTemplate: profileData.assessment_template || 'engineering_institutional_catalyst'
          });
          console.log('🔍 StudentContextGenerator: Loaded academic focus:', {
            focusArea: profileData.assessment_focus_area,
            positioningNarrative: profileData.positioning_narrative,
            assessmentTemplate: profileData.assessment_template
          });

          // Load existing context summary if available
          console.log('🔍 StudentContextGenerator: Strategic context from DB:', profileData.strategic_context_summary);
          console.log('🔍 StudentContextGenerator: Available fields:', Object.keys(profileData));
          setContextSummary(profileData.strategic_context_summary || '');
          setLocalContextSummary(profileData.strategic_context_summary || '');
        }
        
        console.log('🔍 StudentContextGenerator: Data loading completed');
      } catch (error) {
        console.error('Error loading student data:', error);
        setError(`Failed to load student data: ${error.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
        console.log('🔍 StudentContextGenerator: loadStudentData finished');
      }
    };

    // Run immediately without setTimeout
    loadStudentData();
  }, []); // Empty dependency array - effect runs only once on mount

  // Cleanup effect to prevent state updates after unmount
  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  // Helper function to get human-readable labels for personality profile values
  const getPersonalityLabel = (value, category) => {
    const labels = {
      motivation: {
        'achievement': 'wants to excel and be acknowledged',
        'curiosity': 'driven to figure things out and learn',
        'impact': 'motivated by making a difference'
      },
      communication: {
        'data-logic': 'responds to facts, evidence, and logical reasoning',
        'stories-examples': 'learns through narratives and concrete examples',
        'visual-hands-on': 'needs to see it or try it to understand'
      },
      resistance: {
        'authority-skeptical': 'questions "because I said so" reasoning',
        'perfectionist-paralyzed': 'afraid of not doing it perfectly',
        'overwhelm-avoidant': 'shuts down when there\'s too much at once'
      },
      energy: {
        'competition': 'thrives on winning and outperforming others',
        'collaboration': 'energized by working with and helping others',
        'independent-exploration': 'needs space to figure things out alone'
      },
      decision: {
        'research-heavy': 'wants all the data before deciding',
        'intuition-based': 'goes with gut feeling and instinct',
        'consensus-seeking': 'wants input from trusted people'
      },
      feedback: {
        'direct-analytical': 'straightforward, data-based critique',
        'supportive-encouraging': 'positive reinforcement first',
        'challenge-based': 'framed as problems to solve'
      },
      identity: {
        'individual-achiever': 'personal excellence and recognition',
        'community-contributor': 'part of something bigger',
        'system-innovator': 'someone who improves how things work'
      }
    };

    return labels[category]?.[value] || value;
  };

  // Debounced sync for context summary
  useEffect(() => {
    const timer = setTimeout(() => {
      console.log('🔍 Debounced context summary update');
      setContextSummary(localContextSummary);
    }, 300);
    return () => clearTimeout(timer);
  }, [localContextSummary]);

  // Sync local state when contextSummary changes from external sources
  useEffect(() => {
    console.log('🔍 StudentContextGenerator: contextSummary changed to:', contextSummary);
    console.log('🔍 StudentContextGenerator: Setting localContextSummary to:', contextSummary);
    setLocalContextSummary(contextSummary);
  }, [contextSummary]);


  const generatedPromptValue = useMemo(() => {
    console.log('🔍 generatedPromptValue useMemo calculating...');
    
    const hasParentA = Object.values(parentObservations).some(val => val?.trim());
    const hasParentB = Object.values(parentBObservations).some(val => val?.trim());
    
    const prompt = `Generate a Strategic Context Summary AND 4 specific refinement options for this student:

**STUDENT PROFILE:**
Name: ${studentProfile?.name || 'Student'}
${studentProfile?.graduation_year ? `Graduation Year: ${studentProfile.graduation_year}` : ''}
${studentProfile?.grade_level ? `Grade Level: ${studentProfile.grade_level}` : ''}
${studentProfile?.grade_level ? `Student Type: ${getStudentType(studentProfile.grade_level)}` : ''}
${studentProfile?.grade_level ? `Timeline Context: ${calculateTimelineMessage(studentProfile.grade_level).primary}` : ''}
${studentProfile?.state_of_residency ? `State: ${studentProfile.state_of_residency}` : ''}

**ACADEMIC PERFORMANCE:**
${studentProfile?.gpaWeighted ? `GPA Weighted: ${studentProfile.gpaWeighted}` : ''}
${studentProfile?.gpa_unweighted ? `GPA Unweighted: ${studentProfile.gpa_unweighted}` : ''}
${studentProfile?.gpa_scale ? `GPA Scale: ${studentProfile.gpa_scale}` : ''}
${studentProfile?.class_rank && studentProfile?.class_size ? `Class Rank: ${studentProfile.class_rank} of ${studentProfile.class_size}` : ''}
${studentProfile?.class_rank_percentage ? `Class Rank Percentile: ${studentProfile.class_rank_percentage}%` : ''}

**TEST SCORES:**
${studentProfile?.satTotal ? `SAT Total: ${studentProfile.satTotal}` : ''}
${studentProfile?.sat_math ? `SAT Math: ${studentProfile.sat_math}` : ''}
${studentProfile?.sat_reading_writing ? `SAT Reading/Writing: ${studentProfile.sat_reading_writing}` : ''}
${studentProfile?.actComposite ? `ACT Composite: ${studentProfile.actComposite}` : ''}
${studentProfile?.act_math ? `ACT Math: ${studentProfile.act_math}` : ''}
${studentProfile?.act_english ? `ACT English: ${studentProfile.act_english}` : ''}
${studentProfile?.act_reading ? `ACT Reading: ${studentProfile.act_reading}` : ''}
${studentProfile?.act_science ? `ACT Science: ${studentProfile.act_science}` : ''}
${studentProfile?.psat_total ? `PSAT Total: ${studentProfile.psat_total}` : ''}
${studentProfile?.national_merit_status ? `National Merit Status: ${studentProfile.national_merit_status}` : ''}

**ACADEMIC RIGOR:**
${studentProfile?.curriculum_rigor ? `Curriculum Rigor: ${studentProfile.curriculum_rigor}` : ''}
${studentProfile?.ap_courses_taken ? `AP Courses Taken: ${studentProfile.ap_courses_taken}` : ''}
${studentProfile?.ap_courses_planned ? `AP Courses Planned: ${studentProfile.ap_courses_planned}` : ''}
${studentProfile?.ap_exams_passed ? `AP Exams Passed: ${studentProfile.ap_exams_passed}` : ''}
${studentProfile?.ap_courses_details ? `AP Course Details: ${studentProfile.ap_courses_details}` : ''}
${studentProfile?.ib_diploma_candidate ? `IB Diploma Candidate: ${studentProfile.ib_diploma_candidate ? 'Yes' : 'No'}` : ''}
${studentProfile?.ib_predicted_score ? `IB Predicted Score: ${studentProfile.ib_predicted_score}` : ''}
${studentProfile?.dual_enrollment_credits ? `Dual Enrollment Credits: ${studentProfile.dual_enrollment_credits}` : ''}
${studentProfile?.honors_courses ? `Honors Courses: ${studentProfile.honors_courses}` : ''}
${studentProfile?.course_load_description ? `Course Load Description: ${studentProfile.course_load_description}` : ''}

**ACADEMICS & INTERESTS:**
Academic Interests: ${studentProfile?.academicInterests && Array.isArray(studentProfile.academicInterests) ? studentProfile.academicInterests.join(', ') : studentProfile?.academic_interests && Array.isArray(studentProfile.academic_interests) ? studentProfile.academic_interests.join(', ') : 'Not specified'}
Career Goals: ${studentProfile?.careerGoals || studentProfile?.career_goals || 'Not specified'}
Learning Style: ${studentProfile?.learningStyle || studentProfile?.learning_style || 'Not specified'}
Academic Strengths: ${studentProfile?.academicStrengths && Array.isArray(studentProfile.academicStrengths) ? studentProfile.academicStrengths.join(', ') : studentProfile?.academic_strengths && Array.isArray(studentProfile.academic_strengths) ? studentProfile.academic_strengths.join(', ') : 'Not specified'}
Unique Qualities: ${studentProfile?.uniqueQualities || studentProfile?.unique_qualities || 'Not specified'}
Intended Major: ${studentProfile?.intendedMajor || studentProfile?.intended_major || 'Not specified'}
${studentProfile?.intended_minor ? `Intended Minor: ${studentProfile.intended_minor}` : ''}

**ACTIVITIES & LEADERSHIP:**
${studentProfile?.extracurriculars && Array.isArray(studentProfile.extracurriculars) && studentProfile.extracurriculars.length > 0 ? `Extracurriculars: ${studentProfile.extracurriculars.map(activity => `${activity.name || activity} (${activity.category || 'Other'})`).join(', ')}` : ''}
${studentProfile?.leadership_activities ? `Leadership: ${studentProfile.leadership_activities}` : ''}
${studentProfile?.work_experience ? `Work Experience: ${studentProfile.work_experience}` : ''}
${studentProfile?.volunteer_work ? `Volunteer Work: ${studentProfile.volunteer_work}` : ''}

**AWARDS & RECOGNITION:**
${studentProfile?.awards && Array.isArray(studentProfile.awards) && studentProfile.awards.length > 0 ? `Awards: ${studentProfile.awards.map(award => award.name || award).join(', ')}` : ''}
${studentProfile?.academic_awards ? `Academic Awards: ${studentProfile.academic_awards}` : ''}
${studentProfile?.hs_competitiveness ? `High School Competitiveness: ${studentProfile.hs_competitiveness}` : ''}
${studentProfile?.hs_avg_test_scores ? `School Average Test Scores: ${studentProfile.hs_avg_test_scores}` : ''}
${studentProfile?.hs_pct_college_bound ? `% College Bound: ${studentProfile.hs_pct_college_bound}%` : ''}
${studentProfile?.grade_trend ? `Grade Trend: ${studentProfile.grade_trend}` : ''}
${studentProfile?.grade_context ? `Grade Context: ${studentProfile.grade_context}` : ''}
${studentProfile?.special_talents ? `Special Talents: ${studentProfile.special_talents}` : ''}

**BACKGROUND & CONTEXT:**
${studentProfile?.high_school_name ? `High School: ${studentProfile.high_school_name}` : ''}
${studentProfile?.high_school_city && studentProfile?.high_school_state ? `School Location: ${studentProfile.high_school_city}, ${studentProfile.high_school_state}` : ''}
${studentProfile?.high_school_type ? `School Type: ${studentProfile.high_school_type}` : ''}
${studentProfile?.grade_level ? `Grade Level: ${studentProfile.grade_level}` : ''}
${studentProfile?.special_circumstances ? `Special Circumstances: ${studentProfile.special_circumstances}` : ''}
${studentProfile?.first_generation_college !== undefined ? `First Generation College: ${studentProfile.first_generation_college ? 'Yes' : 'No'}` : ''}
${studentProfile?.legacy_connections && Array.isArray(studentProfile.legacy_connections) && studentProfile.legacy_connections.length > 0 ? `Legacy Connections: ${studentProfile.legacy_connections.join(', ')}` : ''}
${studentProfile?.underrepresented_background !== undefined ? `Underrepresented Background: ${studentProfile.underrepresented_background ? 'Yes' : 'No'}` : ''}
${studentProfile?.languages ? `Languages: ${studentProfile.languages}` : ''}
${studentProfile?.student_self_assessment ? `Student Self-Assessment: ${studentProfile.student_self_assessment}` : ''}
${studentProfile?.additional_notes ? `Additional Notes: ${studentProfile.additional_notes}` : ''}

**PERSONALITY PROFILE:**
${personalityProfile?.motivationStyle ? `Primary Motivation: ${personalityProfile.motivationStyle} - ${getPersonalityLabel(personalityProfile.motivationStyle, 'motivation')}` : ''}
${personalityProfile?.communicationPreference ? `Communication Style: ${personalityProfile.communicationPreference} - ${getPersonalityLabel(personalityProfile.communicationPreference, 'communication')}` : ''}
${personalityProfile?.resistancePatterns ? `Resistance Patterns: ${personalityProfile.resistancePatterns} - ${getPersonalityLabel(personalityProfile.resistancePatterns, 'resistance')}` : ''}
${personalityProfile?.energySource ? `Energy Source: ${personalityProfile.energySource} - ${getPersonalityLabel(personalityProfile.energySource, 'energy')}` : ''}
${personalityProfile?.decisionMaking ? `Decision Making: ${personalityProfile.decisionMaking} - ${getPersonalityLabel(personalityProfile.decisionMaking, 'decision')}` : ''}
${personalityProfile?.feedbackStyle ? `Feedback Preference: ${personalityProfile.feedbackStyle} - ${getPersonalityLabel(personalityProfile.feedbackStyle, 'feedback')}` : ''}
${personalityProfile?.identityFramework ? `Identity Framework: ${personalityProfile.identityFramework} - ${getPersonalityLabel(personalityProfile.identityFramework, 'identity')}` : ''}

**ACADEMIC FOCUS & POSITIONING:**
${academicFocus?.focusArea ? `Academic Focus: ${academicFocus.focusArea}` : ''}
${academicFocus?.positioningNarrative ? `Positioning Narrative: ${academicFocus.positioningNarrative}` : ''}
${academicFocus?.assessmentTemplate ? `Assessment Framework: ${academicFocus.assessmentTemplate}` : ''}

${hasParentA ? `**PARENT A OBSERVATIONS:**
${Object.entries(parentObservations)
  .filter(([_, value]) => value?.trim())
  .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${value}`)
  .join('\n')}` : ''}

${hasParentB ? `**PARENT B OBSERVATIONS:**
${Object.entries(parentBObservations)
  .filter(([_, value]) => value?.trim())
  .map(([key, value]) => `${key.replace(/([A-Z])/g, ' $1').toLowerCase()}: ${value}`)
  .join('\n')}` : ''}

**OUTPUT FORMAT:**
STUDENT_CONTEXT_SUMMARY:
[Write a strategic positioning summary that identifies this student's unique value proposition for college admissions. Focus on what makes them distinctive, their authentic strengths, and how their background/experiences create a compelling narrative. This should be 200-300 words that a parent could use as strategic context for creating targeted assessment questions.]

Generate strategic context that helps position this student competitively while staying authentic to who they are.`;

    return prompt;
  }, [
    JSON.stringify(parentObservations),
    JSON.stringify(parentBObservations),
    JSON.stringify(studentProfile),
    JSON.stringify(personalityProfile),
    JSON.stringify(academicFocus)
  ]);

  const copyPromptToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPromptValue);
      setCopySuccess('copied');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setCopySuccess('failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const getRefinementPrompt = () => {
    const studentName = studentProfile?.name || studentProfile?.displayName || '[student name]';
    const academicInterests = studentProfile?.academicInterests?.join(' and ') || 
                             studentProfile?.academic_interests?.join(' and ') || 
                             '[areas of academic interest]';
    
    return `Great! Now review the strategic context profile as follows:

1) Does it position ${studentName} as a student who will enhance the ${academicInterests} programs?

2) Are the themes and overall profile descriptions specific and compelling?

3) Does the context capture the student's unique intellectual signature?

4) Will this output give Claude the necessary context to produce our initial question set (6 questions customized to student that will a) immediately feel relevant, personal, engaging, and intuitive to the student; b) yield answers that can reveal a more nuanced picture of who the student is, strengths/weakness, motivators, potential, compelling and distinctive personal essay angles, and unique value proposition)?

Please revise the strategic context profile to address any gaps identified in questions 1-4 above.`;
  };


  const copyRefinementPromptToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getRefinementPrompt());
      setCopySuccess('refinement');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy refinement prompt:', err);
      setCopySuccess('failed');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };


  const saveContextSummary = async () => {
    if (!currentUser || !contextSummary.trim() || !selectedStudentUserId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('student_academic_profiles')
        .update({
          strategic_context_summary: contextSummary.trim(),
          assessment_phase: 2,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', selectedStudentUserId);

      if (error) {
        console.error('Error saving context summary:', error);
      } else {
        // Update master strategy
        await updateMasterStrategy({
          strategic_context_summary: contextSummary.trim(),
          phase_2_completed: true,
          updated_at: new Date().toISOString()
        });

        console.log('✅ Strategic context saved successfully');
        
        // Navigate to Phase 3
        navigate('/college/question-set-generator');
      }
    } catch (error) {
      console.error('Error saving strategic context:', error);
    } finally {
      setSaving(false);
    }
  };

  const hasRequiredData = useMemo(() => {
    console.log('🔍 hasRequiredData useMemo calculating...');
    console.log('🔍 parentObservations:', parentObservations);
    console.log('🔍 parentBObservations:', parentBObservations);
    console.log('🔍 studentProfile:', studentProfile);
    
    // Count filled parent A observations
    const parentACount = Object.values(parentObservations).filter(val => val?.trim()).length;
    const parentATotal = Object.keys(parentObservations).length;
    
    // Count filled parent B observations  
    const parentBCount = Object.values(parentBObservations).filter(val => val?.trim()).length;
    const parentBTotal = Object.keys(parentBObservations).length;
    
    // Count filled student profile fields
    const studentProfileCount = studentProfile ? [
      studentProfile.academicInterests?.length > 0,
      studentProfile.careerGoals?.trim(),
      studentProfile.learningStyle?.trim(),
      studentProfile.academicStrengths?.length > 0,
      studentProfile.uniqueQualities?.trim(),
      studentProfile.intendedMajor?.trim(),
      studentProfile.gpaWeighted,
      studentProfile.satTotal,
      studentProfile.actComposite
    ].filter(Boolean).length : 0;
    
    const studentProfileTotal = 9; // Total possible student profile fields
    
    console.log('🔍 Counts - Parent A:', parentACount, '/', parentATotal);
    console.log('🔍 Counts - Parent B:', parentBCount, '/', parentBTotal);
    console.log('🔍 Counts - Student Profile:', studentProfileCount, '/', studentProfileTotal);
    
    // More lenient validation - require at least 3 parent observations OR 3 student profile fields
    // OR at least some meaningful combination
    const result = (
      parentACount >= 3 ||
      parentBCount >= 3 ||
      studentProfileCount >= 3 ||
      // Or at least some basic data exists (minimum viable combination)
      (parentACount >= 1 && studentProfileCount >= 2) ||
      (parentBCount >= 1 && studentProfileCount >= 2) ||
      (parentACount >= 2 && parentBCount >= 1) ||
      // Or have test scores and some profile data
      (studentProfile?.gpaWeighted && studentProfileCount >= 2) ||
      (studentProfile?.satTotal && studentProfileCount >= 2) ||
      (studentProfile?.actComposite && studentProfileCount >= 2)
    );
    
    console.log('🔍 hasRequiredData result:', result);
    return result;
  }, [
    JSON.stringify(parentObservations), 
    JSON.stringify(parentBObservations), 
    JSON.stringify(studentProfile)
  ]);

  // Sync local context summary with saved context
  useEffect(() => {
    setContextSummary(localContextSummary);
  }, [localContextSummary]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Loading Student Data</h2>
          <p className="text-slate-600">{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Error Loading Data</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                dataLoaded.current = false;
                window.location.reload();
              }}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
            >
              Retry
            </button>
            <button
              onClick={() => navigate('/college')}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasRequiredData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Missing Required Data</h2>
          <p className="text-slate-600 mb-6">
            To generate strategic context, we need either parent observations or a complete student profile.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/college/parent-observations')}
              className="w-full px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium"
            >
              Complete Parent A Observations
            </button>
            <button
              onClick={() => navigate('/college?tab=profile')}
              className="w-full px-6 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium"
            >
              Complete Student Profile
            </button>
            <button
              onClick={() => navigate('/college')}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/college')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to College Journey
            </button>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-full border border-slate-700 mb-4">
              <Brain className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white">Phase 2: Strategic Context</span>
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              Strategic Context Generator
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Create strategic positioning for your student using browser Claude for optimal AI performance. 
              This context will guide all subsequent assessment questions.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto space-y-8">
          {/* Student Data Summary */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Student Data Summary
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Student Profile */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Student Profile
                </h3>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Name:</span> {studentProfile?.name}</div>
                  {studentProfile?.academicInterests?.length > 0 && (
                    <div><span className="font-medium">Interests:</span> {studentProfile.academicInterests.join(', ')}</div>
                  )}
                  {studentProfile?.careerGoals && (
                    <div><span className="font-medium">Career Goals:</span> {studentProfile.careerGoals}</div>
                  )}
                  {studentProfile?.learningStyle && (
                    <div><span className="font-medium">Learning Style:</span> {studentProfile.learningStyle}</div>
                  )}
                  {studentProfile?.uniqueQualities && (
                    <div><span className="font-medium">Unique Qualities:</span> {studentProfile.uniqueQualities}</div>
                  )}
                </div>
              </div>

              {/* Parent Observations */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Parent Observations
                </h3>
                <div className="space-y-2 text-sm">
                  {Object.values(parentObservations).some(val => val?.trim()) && (
                    <div className="text-emerald-700">✓ Parent A observations complete</div>
                  )}
                  {Object.values(parentBObservations).some(val => val?.trim()) && (
                    <div className="text-emerald-700">✓ Parent B observations complete</div>
                  )}
                  {!Object.values(parentObservations).some(val => val?.trim()) && 
                   !Object.values(parentBObservations).some(val => val?.trim()) && (
                    <div className="text-amber-600">No parent observations available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Step 1: Generate Prompt */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Step 1: Copy Prompt for Browser Claude
            </h2>
            
            <p className="text-slate-600 mb-4">
              Copy this prompt and paste it into browser Claude for optimal strategic thinking. 
              Browser Claude will provide better strategic analysis than our API integration.
            </p>

            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Prompt for Browser Claude</span>
                <button
                  onClick={copyPromptToClipboard}
                  className={`flex items-center gap-2 px-3 py-1 rounded text-sm font-medium ${
                    copySuccess === 'copied' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : copySuccess === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  {copySuccess === 'copied' ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      Copy Prompt
                    </>
                  )}
                </button>
              </div>
              <div className="bg-white rounded border p-3 max-h-64 overflow-y-auto">
                <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                  {generatedPromptValue}
                </pre>
              </div>
            </div>
          </div>

          {/* Step 2: Review & Refine with Claude */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Step 2: Review & Refine with Claude
            </h2>
            
            <p className="text-sm text-slate-700 mb-4">
              Before finalizing, copy and paste this follow-up prompt to Claude to review and refine the strategic context:
            </p>
            
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
              <div className="bg-white rounded border p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-slate-600">Refinement Prompt</span>
                  <button
                    onClick={copyRefinementPromptToClipboard}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                  >
                    <Copy className="w-3 h-3" />
                    {copySuccess === 'refinement' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <pre className="text-xs text-slate-600 whitespace-pre-wrap">
                  {getRefinementPrompt()}
                </pre>
              </div>
              
              <p className="text-xs text-azure-700">
                Use this prompt to ensure the strategic context is compelling and will generate effective assessment questions.
              </p>
            </div>
          </div>

          {/* Step 3: Paste Refined Results */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Step 3: Paste Refined Strategic Context
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Final Strategic Context Summary
                </label>
                <textarea
                  value={localContextSummary}
                  onChange={(e) => setLocalContextSummary(e.target.value)}
                  placeholder="Paste the refined STUDENT_CONTEXT_SUMMARY from Claude here..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 resize-none"
                  rows="8"
                />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/college/parent-observations-b')}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Parent B Observations
            </button>

            <button
              onClick={saveContextSummary}
              disabled={!contextSummary.trim() || saving}
              className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save & Continue to Phase 3
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default StudentContextGenerator;